/**
 * Share a persisted, keyed store with the app's other windows.
 *
 * The desktop app opens documents and agents in DETACHED windows, each with its own JS context and
 * its own copy of every zustand store. Settling a statement in the Document Workspace and seeing
 * Reconcile still complain about it is the same bug every time: two windows, two stores, nothing
 * between them.
 *
 * It cannot be done by watching the persisted write. These stores persist through
 * `createDeferredPersistStorage`, which writes to IndexedDB — no cross-window event exists for it,
 * and the write is debounced besides, so a listener would hear nothing and a rehydrate on a timer
 * would read whatever had landed so far. The change has to be sent.
 *
 * ## Why two transports
 *
 * Both mechanisms this codebase already relies on are used exactly once, and neither is verified
 * against Tauri's multi-webview setup from inside the app:
 *
 * - `BroadcastChannel`, by the document page-edit drafts. Purpose-built, but the spec scopes it to
 *   one agent cluster, and separate WKWebViews are generally separate clusters.
 * - a `localStorage` write plus the `storage` event, by the detached-window launcher handing a
 *   window its payload — which is load-bearing for opening documents at all.
 *
 * Sending over both costs one small message and removes the guess. Receivers are idempotent (the
 * same entry applied twice is the same state) and echo-guarded, so a doubled delivery is harmless.
 *
 * ## Why per-entry
 *
 * Entries travel one at a time, including deletions, rather than as a whole-record snapshot. A
 * window left open across someone else's work holds a stale view of every OTHER key, and shipping
 * the whole record would quietly revert them; shipping only what changed limits a stale window to
 * the entry it actually touched.
 */

interface CrossWindowEntryMessage<TValue> {
  signalKey: string;
  key: string;
  /** Absent means the entry was removed. */
  value?: TValue;
  at: number;
}

export function syncPersistedRecordAcrossWindows<TValue>({
  signalKey,
  select,
  apply,
  subscribe,
}: {
  /** Channel and localStorage key this store announces itself under. Unique per store. */
  signalKey: string;
  select: (state: unknown) => Record<string, TValue | undefined>;
  /** Apply one entry from another window. `value === undefined` means delete the key. */
  apply: (key: string, value: TValue | undefined) => void;
  /** Usually `store.subscribe`, which hands back the new and previous state. */
  subscribe: (listener: (state: unknown, previous: unknown) => void) => void;
}): void {
  if (typeof window === "undefined") return;

  let applyingRemote = false;
  const channel = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel(signalKey) : undefined;

  const receive = (message: CrossWindowEntryMessage<TValue> | undefined) => {
    if (!message?.key || message.signalKey !== signalKey) return;
    applyingRemote = true;
    try {
      apply(message.key, message.value);
    } finally {
      applyingRemote = false;
    }
  };

  if (channel) {
    channel.onmessage = (event: MessageEvent<CrossWindowEntryMessage<TValue>>) => receive(event.data);
  }
  window.addEventListener("storage", (event) => {
    if (event.key !== signalKey || !event.newValue) return;
    try {
      receive(JSON.parse(event.newValue) as CrossWindowEntryMessage<TValue>);
    } catch {
      // A malformed signal is not worth breaking a window over; it just stays independent.
    }
  });

  subscribe((state, previous) => {
    // Never echo a change back to the window it came from.
    if (applyingRemote) return;
    const next = select(state);
    const before = select(previous);
    if (next === before) return;
    for (const key of new Set([...Object.keys(next), ...Object.keys(before)])) {
      if (next[key] === before[key]) continue;
      const message: CrossWindowEntryMessage<TValue> = {
        signalKey,
        key,
        ...(next[key] === undefined ? {} : { value: next[key] }),
        // `at` makes the stored string differ when the same entry is sent twice — `storage` only
        // fires when the value actually changes.
        at: Date.now(),
      };
      channel?.postMessage(message);
      try {
        localStorage.setItem(signalKey, JSON.stringify(message));
      } catch {
        // Quota, or a private window. Losing one transport still leaves the other.
      }
    }
  });
}
